import React from 'react';
import type { MessageDescriptor } from 'react-intl-next';
import { defineMessages, useIntl } from 'react-intl-next';

import type { ClassificationOption, Color } from '@atlassian/data-classification-level';
import DataClassificationLevel from '@atlassian/data-classification-level';

import { ErrorDisplay } from '@confluence/error-boundary';

import {
	useTrackClassificationAnalytics,
	ActionSubjectId,
} from '../hooks/useTrackClassificationAnalytics';
import { getPopupPlacementForTagVariant } from '../utils/getPopupPlacementForTagVariant';
import { TagButtonUIVariant } from '../constants/TagButtonUIVariant';
import { useComputeDefaultClassificationDropdownOption } from '../hooks/useComputeDefaultClassificationDropdownOption';
import type { ClassificationLevel } from '../ClassificationRadioOption';

import { ClassificationEditViewPopup } from './ClassificationEditViewPopup';

type GenericAddClassificationButtonProps = {
	spaceKey: string;
	getClassificationLevelFromId: (id?: string | null) => ClassificationLevel | undefined;
	buttonLabel?: MessageDescriptor;
	onEditCancel?: () => void;
	onEditSubmit?: (c: ClassificationOption) => void;
	loading?: boolean;
	variant?: TagButtonUIVariant;
};

const messages = defineMessages({
	addButtonLabel: {
		id: 'data-classification.add-button.default-label',
		description:
			'Text in the button below breadcrumbs to prompt user to add classification in title.',
		defaultMessage: 'Add classification',
	},
});

const DataClassificationLevelMemo = React.memo(DataClassificationLevel);

export const GenericAddClassificationButton = ({
	spaceKey,
	getClassificationLevelFromId,
	buttonLabel: buttonLabelProp,
	onEditCancel: onEditCancelProp,
	onEditSubmit: onEditSubmitProp,
	loading = false,
	variant = TagButtonUIVariant.default,
}: GenericAddClassificationButtonProps) => {
	const { formatMessage } = useIntl();
	const { trackEditPopupViewed } = useTrackClassificationAnalytics();
	const popupPlacement = getPopupPlacementForTagVariant(variant);

	const onEditCancel = (closePopup: () => void) => {
		onEditCancelProp && onEditCancelProp();
		closePopup();
	};

	const onEditSubmit = (classification: ClassificationOption, closePopup: () => void) => {
		onEditSubmitProp && onEditSubmitProp(classification);
		closePopup();
	};

	const handlePopupOpen = () => {
		trackEditPopupViewed(ActionSubjectId.CONTENT_TYPE_TITLE_DATA_CLASSIFICATION_POPUP);
	};

	const buttonLabel = buttonLabelProp || messages.addButtonLabel;

	// Note: space-default left out here, as this 'add' button
	// should only show up when there is no effective content-classification
	const { defaultClassificationOption, error: defaultClassificationError } =
		useComputeDefaultClassificationDropdownOption({
			getClassificationLevelFromId,
		});

	return (
		<>
			{defaultClassificationError ? <ErrorDisplay error={defaultClassificationError} /> : null}
			<DataClassificationLevelMemo
				testId="data-classification-add-new-popup"
				name={formatMessage(buttonLabel)}
				color={'GREY' as Color}
				renderPopupComponent={
					// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
					!process.env.REACT_SSR
						? (closePopup: () => void) => (
								<ClassificationEditViewPopup
									spaceKey={spaceKey}
									defaultOption={defaultClassificationOption}
									onEditCancel={() => onEditCancel(closePopup)}
									onEditSubmit={(classification) => onEditSubmit(classification, closePopup)}
									loading={loading}
								/>
							)
						: undefined
				}
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				onPopupShown={!process.env.REACT_SSR ? () => handlePopupOpen() : undefined}
				isIconOnly={variant === TagButtonUIVariant.iconOnly}
				popupPlacement={popupPlacement}
			/>
		</>
	);
};
